import {
  Booking,
  BookingRequest,
  BookingStatus,
  User,
  Vehicle,
} from '../../@generated/schemas';
import { Box, IconButton, Theme as MuiTheme, useMediaQuery } from '@mui/material';
import TableContainer, { TableContainerTypeMap } from '@mui/material/TableContainer';
import styled, { css } from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import {
  useDataProvider,
  useDelete,
  useNotify,
  useUpdate,
} from 'react-admin';

import { BookingSchedulerDialog } from './schedulerBookingDialog';
import ConfirmationDialog from '../../common/ConfirmDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { LoadingElement } from '../../App';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import Paper from '@mui/material/Paper';
import { ParsedDatesRange } from '../../libs/scheduler/utils/getDatesRange';
import { RequestBookingSchedulerDialog } from './schedulerRequestBookingDialog';
import { Scheduler } from '../../libs/scheduler';
import { SchedulerData } from '../../libs/scheduler';
import { SchedulerProjectData } from '../../libs/scheduler/types/global';
import { SchedulerVehicleMap } from './schedulerVehicleMap';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { useTranslate } from '../../locales';

export const StyledSchedulerFrame = styled.div<{isSmall: boolean}>`
  position: relative;
  height: calc(80vh - 100px);
  //width: 100vw;

  ${({isSmall}) => isSmall ? css`
    width: 100vw;
  ` : ''}
`;

export const StyledTableContainer = styled(TableContainer)<any>`
  position: relative;
  height: calc(25vh);
  //width: (100vw - 300px);

  @media screen and (max-width: 768px) {
    width: 100vw !important;
  }
` as OverridableComponent<TableContainerTypeMap>;

export const LoadingElementWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.5);
`;

export const ModalBox = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  background-color: white;
  border: 1px solid #f3f3f3;
  border-radius: 4px;
  padding: 6px 12px;
  box-shadow: 24px;
`;

export const BookingScheduler = () => {
  const translate = useTranslate();
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [vehicleMapDetail, setVehicleMapDetail] = useState(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingRequests, setBookingRequests] = useState<BookingRequest[]>([]);
  const [updatingBooking, setUpdatingBooking] = useState(false);
  const [selectedBooking, setSelectedBooking] =
    useState<SchedulerProjectData>(null);
  const [selectedBookingRequest, setSelectedBookingRequest] =
    useState<Partial<BookingRequest>>(null);
  const [schedulerData, setSchedulerData] = useState<SchedulerData>([]);
  const isSmall = useMediaQuery((theme: MuiTheme) =>
    theme.breakpoints.down('sm'),
  );
  // const [range, setRange] = useState<ParsedDatesRange>(getParsedDatesRange(dayjs(), 2));
  const [range, setRange] = useState<ParsedDatesRange>({
    startDate: null,
    endDate: null
  });
  const refetchBookings = async (
    showLoading = true,
    range: ParsedDatesRange,
  ) => {
    if (!range.startDate || !range.endDate) {
      return false;
    }
    const identity = JSON.parse(localStorage.getItem('user'));

    showLoading && setUpdatingBooking(true);

    const { data: _bookings } = await dataProvider.getList<Booking>('Booking', {
      pagination: {
        page: 1,
        perPage: 100000,
      },
      sort: {
        field: 'id',
        order: 'ASC',
      },
      filter: {
        startDate: { lte: range.endDate }, // Booking starts after or on startDate
        endDate: { gte: range.startDate }, // Booking ends before or on endDate
        cancelDate: {
          equals: null,
        },
      },
    });

    const { data: _bookingRequests } =
      await dataProvider.getList<BookingRequest>('BookingRequest', {
        pagination: {
          page: 1,
          perPage: 100000,
        },
        sort: {
          field: 'id',
          order: 'ASC',
        },
        filter: identity?.companyId
          ? {
              user: {
                is: {
                  company: {
                    is: {
                      id: {
                        equals: identity?.companyId,
                      },
                    },
                  },
                },
              },
            }
          : undefined,
      });

    setBookings(_bookings);
    setBookingRequests(_bookingRequests);

    showLoading && setUpdatingBooking(false);
  };

  const refetchAll = async (showLoading = true) => {
    showLoading && setUpdatingBooking(true);
    const { data: _vehicles } = await dataProvider.getList<Vehicle>('Vehicle', {
      pagination: {
        page: 1,
        perPage: 10000,
      },
      sort: {
        field: 'id',
        order: 'ASC',
      },
      filter: {
        deletedAt: {
          equals: null,
        },
      },
    });

    const { data: _users } = await dataProvider.getList<User>('User', {
      pagination: {
        page: 1,
        perPage: 10000,
      },
      sort: {
        field: 'id',
        order: 'ASC',
      },
      filter: {
        deletedAt: {
          equals: null,
        },
      },
    });

    const userOptions = (_users || []).map((user) => ({
      id: user.id,
      label:
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName} (${user.email})`
          : user.email,
    }));

    setUsers(userOptions);
    setVehicles(_vehicles);

    showLoading && setUpdatingBooking(false);
  };

  useEffect(() => {
    refetchAll(false);
  }, []);

  const handleRangeChange = useCallback((range: ParsedDatesRange) => {
    setRange(range);
    refetchBookings(true, range);
  }, []);

  useEffect(() => {
    const _vehicles = {};
    for (const vehicle of vehicles || []) {
      if (!_vehicles[vehicle.id]) {
        _vehicles[vehicle.id] = {
          id: vehicle.id,
          label: {
            title: vehicle.plateNumber,
            subtitle: `${
              vehicle.name
                ? vehicle.name
                : `${vehicle.brandType.name} ${vehicle.modelType.name}`
            }`,
            convadis: vehicle.convadisVehicle
              ? `Convadis: ${vehicle.convadisVehicle || ''}, ${
                  vehicle.convadisOrganisation || ''
                }`
              : '',
          },
          collapsed: true,
          data: [],
        };
      }
    }

    for (const booking of bookings || []) {
      if (_vehicles[booking.vehicleId]) {
        let _bgColor = '#728DE2';
        switch(booking.bookingStatus) {
          case BookingStatus.Aborted:
          case BookingStatus.Finished:
            _bgColor = '#d0eef0';
            break;
            case BookingStatus.WaitingForUser:
            _bgColor = '#f3cf7c';
            break;
          case BookingStatus.Running:
            _bgColor = 'green';
            break;
          case BookingStatus.Revoked:
            _bgColor = '#fdb990';
            break;
          case BookingStatus.Late:
            _bgColor = 'red';
            break;
        }
        _vehicles[booking.vehicleId].data.push({
          id: booking.id,
          bgColor: _bgColor,
          vehicleId: booking.vehicleId,
          userId: booking.userId,
          startDate: booking.startDate,
          effectiveStartDate: booking.effectiveStartDate,
          effectiveEndDate: booking.effectiveEndDate,
          bookingStatus: booking.bookingStatus,
          adminBookingComment: booking.adminBookingComment,
          pickupLocationNotes: booking.pickupLocationNotes,
          returnLocationNotes: booking.returnLocationNotes,
          endDate: booking.cancelDate || booking.revokedAt || booking.effectiveEndDate || booking.endDate,
          title: `${booking.user.lastName} ${booking.user.firstName}`,
          description: booking.bookingHash,
          subtitle: booking.convadisBookingIncrement,
        });
      }
    }

    setSchedulerData(Object.values(_vehicles) || []);
  }, [bookings, vehicles, range.endDate, range.startDate]);

  const handleFilterData = () => console.log(`Filters button was clicked.`);

  const handleTileClick = (data: SchedulerProjectData) =>
    setSelectedBooking(data);

  const onVehicleClick = (vehicleId: string) => {
    setSchedulerData((prevData) => {
      return prevData.map((vehicle) => {
        if (vehicle.id === vehicleId) {
          return {
            ...vehicle,
            collapsed: !vehicle.collapsed,
          };
        }

        return vehicle;
      });
    });
  };

  const onItemDrop = (from, to) => {
    //
  };
  const handleItemResize = async (bookingId: string, newEndDate: Date) => {
    setUpdatingBooking(true);

    const booking = bookings.find((booking) => booking.id === bookingId);

    if (booking) {
      await update('Booking', {
        id: booking.id as string,
        data: {
          startDate: dayjs(booking.startDate).format(),
          endDate: dayjs(newEndDate).format(),
          user: booking.userId,
          vehicle: booking.vehicleId,
          adminBookingComment: booking.adminBookingComment,
        },
      });

      setUpdatingBooking(false);

      await refetchBookings(true, range);
    }
  };
  const handleCreateBooking = async (startDate: Date, vehicleId: string) => {
    setUpdatingBooking(true);

    const newBooking: SchedulerProjectData = {
      id: 'NEW',
      vehicleId,
      userId: null,
      startDate,
      effectiveStartDate: null,
      effectiveEndDate: null,
      adminBookingComment: '',
      pickupLocationNotes: '',
      returnLocationNotes: '',
      endDate: dayjs(startDate).add(1, 'hours').toDate(),
      title: 'New booking',
      occupancy: 0,
    };

    setSelectedBooking(newBooking);

    setUpdatingBooking(false);
  };

  const handleSelectRow = (bookingRequest: BookingRequest) => {
    setUpdatingBooking(true);

    const newBooking: SchedulerProjectData = {
      id: 'NEW',
      vehicleId: null,
      bookingRequestId: bookingRequest.id,
      userId: bookingRequest.userId,
      effectiveStartDate: null,
      effectiveEndDate: null,
      startDate: bookingRequest.startDate,
      endDate: bookingRequest.endDate,
      adminBookingComment: bookingRequest.note,
      pickupLocationNotes: bookingRequest.pickupLocationNotes,
      returnLocationNotes: bookingRequest.returnLocationNotes,
      title: 'New booking',
      occupancy: 0,
    };

    setSelectedBooking(newBooking);

    setUpdatingBooking(false);
  };

  const handleNewRequest = () => {
    setUpdatingBooking(true);

    const newRequestBooking: Partial<BookingRequest> = {
      id: 'NEW',
      userId: null,
      startDate: dayjs().toDate(),
      endDate: dayjs().add(1, 'hours').toDate(),
    };

    setSelectedBookingRequest(newRequestBooking);

    setUpdatingBooking(false);
  };

  const handleOpenBookingRequest = (event: any, data: BookingRequest) => {
    event.stopPropagation();

    setSelectedBookingRequest(data);
  };

  const handleDeleteRequestBooking = async (id: string) => {
    setUpdatingBooking(true);

    deleteOne(
      'BookingRequest',
      {
        id,
      },
      {
        onError: async () => {
          setUpdatingBooking(false);
        },
        onSuccess: async () => {
          notify(translate('admin.requestedBookingWasSuccessfullyDeleted'), {
            type: 'success',
          });

          await refetchBookings(true, {
            startDate: range?.startDate,
            endDate: range?.endDate,
          });
          setSelectedBookingRequest(null);
        },
      },
    );
  };

  const onVehicleMapDetailClick = (vehicleId: string) => {
    setVehicleMapDetail(vehicleId);
  };

  return (
    <div>
      <StyledSchedulerFrame isSmall={isSmall}>
        {updatingBooking ? (
          <LoadingElementWrapper>
            <LoadingElement />
          </LoadingElementWrapper>
        ) : null}
        <Scheduler
          // startDate={values.startDate}
          onItemDrop={onItemDrop}
          onClickNewRequest={handleNewRequest}
          onRangeChange={handleRangeChange}
          data={schedulerData}
          // isLoading={updatingBooking}
          onVehicleClick={onVehicleClick}
          onVehicleMapDetailClick={onVehicleMapDetailClick}
          onTileClick={handleTileClick}
          onFilterData={handleFilterData}
          onItemResize={handleItemResize}
          onItemCreate={handleCreateBooking}
          config={{
            zoom: 2,
            lang: i18next.language,
            showThemeToggle: true,
            filterButtonState: null,
          }}
        />
      </StyledSchedulerFrame>

      <BookingSchedulerDialog
        bookingId={selectedBooking?.id}
        bookingRequestId={selectedBooking?.bookingRequestId}
        loading={updatingBooking}
        vehicles={vehicles}
        users={users}
        setLoading={setUpdatingBooking}
        userId={selectedBooking?.userId}
        adminBookingComment={selectedBooking?.adminBookingComment}
        startDate={selectedBooking?.startDate}
        endDate={selectedBooking?.endDate}
        effectiveStartDate={selectedBooking?.effectiveStartDate}
        effectiveEndDate={selectedBooking?.effectiveEndDate}
        bookingStatus={selectedBooking?.bookingStatus}
        vehicleId={selectedBooking?.vehicleId}
        description={selectedBooking?.description}
        pickupLocationNotes={selectedBooking?.pickupLocationNotes}
        returnLocationNotes={selectedBooking?.returnLocationNotes}
        subtitle={selectedBooking?.subtitle}
        refetchBookings={() => refetchBookings(true, range)}
        resetSelectedBooking={() => setSelectedBooking(null)}
        setSelectedBookingParam={(param, value) =>
          setSelectedBooking({
            ...selectedBooking,
            [param]: value,
          })
        }
      />

      <RequestBookingSchedulerDialog
        id={selectedBookingRequest?.id}
        loading={updatingBooking}
        users={users}
        setLoading={setUpdatingBooking}
        userId={selectedBookingRequest?.userId}
        startDate={selectedBookingRequest?.startDate}
        endDate={selectedBookingRequest?.endDate}
        note={selectedBookingRequest?.note}
        pickupLocationNotes={selectedBookingRequest?.pickupLocationNotes}
        returnLocationNotes={selectedBookingRequest?.returnLocationNotes}
        refetchBookings={() => refetchBookings(true, range)}
        resetSelectedRequestBooking={() => setSelectedBookingRequest(null)}
        setSelectedRequestBookingParam={(param, value) =>
          setSelectedBookingRequest({
            ...selectedBookingRequest,
            [param]: value,
          })
        }
      />

      <SchedulerVehicleMap
        vehicleId={vehicleMapDetail}
        handleClose={() => setVehicleMapDetail(null)}
      />

      <StyledTableContainer component={Paper}>
        <Table sx={isSmall ? {width: '100vw'} : { minWidth: 650 }} size="small" aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{translate('admin.user')}</TableCell>
              <TableCell>{translate('admin.startDate')}</TableCell>
              <TableCell>{translate('admin.endDate')}</TableCell>
              <TableCell>{translate('admin.createdBy')}</TableCell>
              <TableCell>{translate('admin.notes')}</TableCell>
              <TableCell>{translate('admin.pickupLocationNotes')}</TableCell>
              <TableCell>{translate('admin.returnLocationNotes')}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookingRequests.map((row) => (
              <TableRow
                key={row.id}
                style={{ cursor: 'pointer' }}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell
                  onClick={() => handleSelectRow(row)}
                  component="th"
                  scope="row">
                  {row.user.firstName} {row.user.lastName} ({row.user.email})
                </TableCell>
                <TableCell onClick={() => handleSelectRow(row)}>
                  {dayjs(row.startDate).format('DD.MM. YYYY HH:mm')}
                </TableCell>
                <TableCell onClick={() => handleSelectRow(row)}>
                  {dayjs(row.endDate).format('DD.MM. YYYY HH:mm')}
                </TableCell>
                <TableCell
                  onClick={() => handleSelectRow(row)}
                  component="th"
                  scope="row">
                  {row.createdBy?.email}
                </TableCell>
                <TableCell
                  onClick={() => handleSelectRow(row)}
                  component="th"
                  scope="row">
                  {row.note}
                </TableCell>
                <TableCell
                  onClick={() => handleSelectRow(row)}
                  component="th"
                  scope="row">
                  {row.pickupLocationNotes}
                </TableCell>
                <TableCell
                  onClick={() => handleSelectRow(row)}
                  component="th"
                  scope="row">
                  {row.returnLocationNotes}
                </TableCell>
                <TableCell align="right" onClick={() => handleSelectRow(row)}>
                  <IconButton
                    size="small"
                    onClick={(event: any) =>
                      handleOpenBookingRequest(event, row)
                    }>
                    <EditIcon />
                  </IconButton>

                  <ConfirmationDialog
                    title={translate('admin.confirmationOfDelete')}
                    description={translate(
                      'admin.confirmationOfDeleteAreYouSure',
                    )}
                    response={() => handleDeleteRequestBooking(row.id)}>
                    {(showDialog) => (
                      <IconButton
                        size="small"
                        style={{ marginLeft: 12 }}
                        onClick={showDialog}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </ConfirmationDialog>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </div>
  );
};
